import React, { useState, useEffect, useLayoutEffect } from 'react';
import NodeBackend from '../../Resources/NodeBackend';
import { Unlock, Hash, DollarSign, PieChart } from 'react-feather';
import { useSettingsManager } from '../../Configuration/SettingsManager';
import NextStockTitleMark from "../../components/NextStockTitleMark";
import CompanyLogos from '../../components/CompanyLogos';
import Header from '../../Static/Header';
import Footer from "../../Static/Footer";
import './Portfolio.css';
import { useTranslation } from "react-i18next";

import {
  portfolioThreshold,
  portfolioAccessRules,
  tierDisplayLabel,
  normalizeClubTier,
  handleSelectionChange
} from './PortfolioAux';

const API_ENDPOINT = new NodeBackend().getPath();

export default function Portfolio({ setPage }) {
  // Always open the Portfolio page at the top, regardless of the previous page's scroll.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { t, i18n } = useTranslation();
  const { user, loadUserAccess, handleSettingsPress } = useSettingsManager();

  const [loading, setLoading] = useState(true);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState('10K USD / 14K CAD');
  const [portfolioData, setPortfolioData] = useState([]);
  const [portfolioType, setPortfolioType] = useState("balanced");

  const TRIAL_MAX_PORTFOLIO = 3;
  const CONSERVATIVE_DIFFERENT_FROM_LEVEL = 6;

  useEffect(() => {
    if (selectedPortfolio) {
      handleSelectionChange({
        portfolioLabel: selectedPortfolio,
        portfolioType,
        API_ENDPOINT,
        CONSERVATIVE_DIFFERENT_FROM_LEVEL,
        setPortfolioData,
        setSelectedPortfolio,
        setPortfolioLoading
      });
    }
  }, [portfolioType]);

  useEffect(() => {
    let isMounted = true;

    async function init() {
      try {
        await loadUserAccess();
      } finally {
        if (isMounted) {
          setLoading(false);
          handleSelectionChange({
            portfolioLabel: '10K USD / 14K CAD',
            portfolioType,
            API_ENDPOINT,
            CONSERVATIVE_DIFFERENT_FROM_LEVEL,
            setPortfolioData,
            setSelectedPortfolio,
            setPortfolioLoading
          });
        }
      }
    }

    init();
    return () => { isMounted = false; };
  }, []);

  const isPremium = (user?.plan?.type || user?.planType) === "premium";
  let normalizedTier = normalizeClubTier(user?.plan?.clubTier || user?.clubTier);
  if (!isPremium) normalizedTier = "none";

  const isTrial = user?.trial === true;
  const maxPortfolio = isTrial
    ? TRIAL_MAX_PORTFOLIO
    : (portfolioAccessRules[normalizedTier] || 8);

  const currentTierLabel = tierDisplayLabel[normalizedTier] || "No Club tier";

  const isPremiumActive =
    user.hasTop10Access === true ||
    (user.planType === 'premium' && user.trial === true);

  const handlePages = (page) => {
    setPage(page);
  };

  const isLessThanPortfolio =
    typeof selectedPortfolio === "string" &&
    selectedPortfolio.toLowerCase().includes("less than");

  const formatMoney = (value) => {
    const number = parseFloat(value);

    if (Number.isNaN(number)) return "-";

    return number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getCurrency = (symbol) => {
    return symbol?.includes(".") ? "CAD" : "USD";
  };

  const getTotalCost = (item) => {
    const price = parseFloat(item.propertyRegularMarketPrice);
    const quantity = parseFloat(item.propertyQuantity);

    if (Number.isNaN(price) || Number.isNaN(quantity)) return "-";

    return formatMoney(price * quantity);
  };

  return (
    <div className="top10-container">

      <Header setPage={handlePages} />

      <div className="top10-content">
        {loading ? (
          <div className="top10-loading">
            <div className="top10-spinner"></div>
          </div>
        ) : (
          <div className="top10-box">
            <div className="portfolio-title-row">
              <NextStockTitleMark className="top10-logo-next-stock" />
              <h2 className="portfolio-title">{t("portfolio.title")}</h2>
              <NextStockTitleMark className="top10-logo-next-stock" />
            </div>

            <p className="portfolio-subtitle">
              <strong>{t("portfolio.usaCanada")}</strong>
            </p>

            {user.planType === 'premium' && user.trial ? (
              <p className="portfolio-plan-line">
                {t("portfolio.plan")}: <strong>{t("portfolio.freeTrial")}</strong>
              </p>
            ) : (
              <div className="portfolio-plan-row">
                {currentTierLabel ? (
                  <p className="portfolio-tier-line">
                    {t("portfolio.clubTier")}:{" "}
                    <strong>{(currentTierLabel || 'None').toUpperCase()}</strong>
                    <span>|</span>
                  </p>
                ) : null}

                <p className="portfolio-plan-line">
                  {t("portfolio.plan")}:{" "}
                  <strong>{(user.plan?.type || user.planType || 'Free').toUpperCase()}</strong>
                </p>
              </div>
            )}

            {isPremiumActive && (
              <p className="portfolio-description">
                📌 {t("portfolio.tierDescription")}
              </p>
            )}

            {isPremiumActive ? (
              <div className="portfolio-controls">
                <div className="portfolio-toggle-row">
                  <div className="portfolio-toggle">
                    <button
                      className={portfolioType === "balanced" ? "active" : ""}
                      onClick={() => setPortfolioType("balanced")}
                    >
                      Balanced
                    </button>

                    <button
                      className={portfolioType === "conservative" ? "active" : ""}
                      onClick={() => setPortfolioType("conservative")}
                    >
                      Conservative
                    </button>
                  </div>

                  {portfolioLoading && <div className="top10-spinner small" />}
                </div>

                {selectedPortfolio && (
                  <p className="portfolio-balanced-note">
                    {portfolioThreshold[selectedPortfolio] <= 5 &&
                      t("portfolio.balancedNote")}
                  </p>
                )}

                <select
                  value={selectedPortfolio}
                  onChange={(e) =>
                    handleSelectionChange({
                      portfolioLabel: e.target.value,
                      portfolioType,
                      API_ENDPOINT,
                      CONSERVATIVE_DIFFERENT_FROM_LEVEL,
                      setPortfolioData,
                      setSelectedPortfolio,
                      setPortfolioLoading
                    })
                  }
                >
                  <option value="">{t("portfolio.selectPortfolio")}</option>

                  {Object.keys(portfolioThreshold).map((label) => {
                    const required = portfolioThreshold[label];
                    const isLocked = required > maxPortfolio;

                    return (
                      <option
                        key={label}
                        value={label}
                        disabled={isLocked}
                        className={isLocked ? "portfolio-option-locked" : ""}
                        title={isLocked ? "Upgrade your plan" : ""}
                      >
                        {label}{isLocked ? " 🔒" : ""}
                      </option>
                    );
                  })}
                </select>

                {maxPortfolio < 18 && (
                  <div className="portfolio-upgrade-hint">
                    {t("portfolio.wishUpgrade")}{" "}
                    (<strong>{currentTierLabel}</strong>)?{" "}
                    <strong onClick={handleSettingsPress}>
                      {t("portfolio.ClickHere")}
                    </strong>
                  </div>
                )}
              </div>
            ) : (
              <button className="top10-upgrade-btn" onClick={handleSettingsPress}>
                <Unlock /> {t("portfolio.upgradePlan")}
              </button>
            )}

            {isPremiumActive && selectedPortfolio && (
              <div className="portfolio-list-container">
                {portfolioLoading ? (
                  <div className="portfolio-loading-area">
                    <div className="top10-spinner"></div>
                  </div>
                ) : Array.isArray(portfolioData) && portfolioData.length > 0 ? (
                  <ul>
                    {portfolioData.map((item, index) => {
                      const currency = getCurrency(item.propertySymbol);
                      const totalCost = getTotalCost(item);
                      const portfolioPercent =
                        typeof item.propertyPortfolioPercent === "number"
                          ? item.propertyPortfolioPercent.toFixed(1)
                          : "-";

                      return (
                        <li key={index} className="portfolio-card-li" style={{ "--stock-index": index }}>
                          <div className="portfolio-card">
                            <div className="portfolio-card-header">
                              <div className="portfolio-card-title-row">
                                <div className="portfolio-rank-badge">
                                  {index + 1}
                                </div>

                                <h4>
                                  {item.propertyShortName} ({item.propertySymbol})
                                </h4>
                              </div>

                              <div className="portfolio-logo-area">
                                <CompanyLogos symbol={item.propertySymbol} />
                              </div>
                            </div>

                            <div className="portfolio-main-metrics">
                              <div className="portfolio-metric-box shares">
                                <Hash size={26} />
                                <div>
                                  <span>
                                    {(isLessThanPortfolio
                                      ? t("portfolio.eachShare")
                                      : t("portfolio.shares")
                                    ).charAt(0).toUpperCase() +
                                      (isLessThanPortfolio
                                        ? t("portfolio.eachShare")
                                        : t("portfolio.shares")
                                      ).slice(1)}
                                  </span>

                                  <strong>
                                    {isLessThanPortfolio
                                      ? `$${formatMoney(item.propertyRegularMarketPrice)}`
                                      : item.propertyQuantity}
                                  </strong>

                                  <small>
                                    {isLessThanPortfolio
                                      ? currency
                                      : (`${t("portfolio.shares")} total`)}
                                  </small>
                                </div>
                              </div>

                              <div className="portfolio-metric-box total-cost">
                                <DollarSign size={26} />
                                <div>
                                  <span>
                                    {isLessThanPortfolio
                                      ? "Share Price"
                                      : t("portfolio.totalCost")}
                                  </span>

                                  <strong>
                                    {isLessThanPortfolio
                                      ? `$${formatMoney(item.propertyRegularMarketPrice)}`
                                      : `$${totalCost}`}
                                  </strong>

                                  <small>{currency}</small>
                                </div>
                              </div>

                              <div className="portfolio-metric-box allocation">
                                <PieChart size={26} />
                                <div>
                                  <span>% {t("portfolio.ofPortfolio")}</span>
                                  <strong>{portfolioPercent}%</strong>
                                  <small>Allocation</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="portfolio-no-data">
                    ⚠️ {t("portfolio.noData")} <strong>{selectedPortfolio}</strong>.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}